import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Add,
  Close,
  PlayArrow,
  YouTube,
  MoreVert,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import { URL } from "../constants/url";
import colors from "../constants/colors";

const Videos = ({ adminData, searchText }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);

  const userId = adminData?._id;
  const ITEMS_PER_PAGE = 9;
  const videosContainerRef = useRef(null);

  // Scroll to top when page changes
  useEffect(() => {
    if (videosContainerRef.current) {
      videosContainerRef.current.scrollTop = 0;
    }
  }, [page]);

  const [formData, setFormData] = useState({
    title: "",
    url: "",
  });

  const [editFormData, setEditFormData] = useState({
    title: "",
    url: "",
  });

  // Filter videos based on search text and only show valid YouTube links
  const filteredVideos = useMemo(() => {
    let filtered = videos;

    // Filter only videos with valid links and type 'link'
    filtered = filtered.filter(
      (video) =>
        video.type === "link" &&
        video.link &&
        typeof video.link === "string" &&
        video.link.trim() !== ""
    );

    // Apply search filter
    if (searchText && searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (video) =>
          video.title?.toLowerCase().includes(searchLower) ||
          video.link?.toLowerCase().includes(searchLower) ||
          video.description?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [videos, searchText]);

  const paginatedVideos = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return filteredVideos.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredVideos, page]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredVideos.length / ITEMS_PER_PAGE));
    setPage(1);
  }, [filteredVideos]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (searchText && searchText.trim() !== "") {
        queryParams.append("search", searchText);
      }
      // Only fetch link type videos
      queryParams.append("type", "link");

      const res = await fetch(
        `${URL}/api/charcha/get-all-videos?${queryParams}`
      );
      const data = await res.json();
      setVideos(data?.data || []);
    } catch (error) {
      console.error("Error fetching videos:", error);
      showAlert("Error fetching videos", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
    // Auto hide after 4 seconds
    setTimeout(() => {
      setAlert({ open: false, message: "", severity: "success" });
    }, 4000);
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "success" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((p) => ({ ...p, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ title: "", url: "" });
  };

  const resetEditForm = () => {
    setEditFormData({ title: "", url: "" });
    setEditingVideo(null);
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setDialogLoading(true);

  //   try {
  //     const submitData = new FormData();
  //     submitData.append("title", formData.title);
  //     submitData.append("type", "link");
  //     submitData.append("url", formData.url);
  //     submitData.append("userId", userId);

  //     const res = await fetch(`${URL}/api/charcha/add-video`, {
  //       method: "POST",
  //       body: submitData,
  //     });

  //     const result = await res.json();

  //     if (result.success) {
  //       setOpenDialog(false);
  //       resetForm();
  //       showAlert("Video added successfully!");
  //       fetchVideos();
  //     } else {
  //       setOpenDialog(false);
  //       resetForm();
  //       showAlert(result.message, "error");
  //     }
  //   } catch (error) {
  //     setOpenDialog(false);
  //     resetForm();
  //     showAlert("Error adding video", "error");
  //   } finally {
  //     setDialogLoading(false);
  //   }
  // };
const handleSubmit = async (e) => {
  e.preventDefault();
  setDialogLoading(true);

  // ADD THIS CHECK
  if (!userId) {
    console.error("User ID is undefined. User might not be logged in.");
    showAlert("Error: User not logged in. Please login again.", "error");
    setDialogLoading(false);
    return;
  }

  try {
    const submitData = new FormData();
    submitData.append("title", formData.title);
    submitData.append("type", "link");
    submitData.append("url", formData.url);
    submitData.append("userId", userId); // Make sure this is not undefined

    console.log("Submitting with userId:", userId); // Debug log

    const res = await fetch(`${URL}/api/charcha/add-video`, {
      method: "POST",
      body: submitData,
    });

    const result = await res.json();

    if (result.success) {
      setOpenDialog(false);
      resetForm();
      showAlert("Video added successfully!");
      fetchVideos();
    } else {
      setOpenDialog(false);
      resetForm();
      showAlert(result.message, "error");
    }
  } catch (error) {
    setOpenDialog(false);
    resetForm();
    showAlert("Error adding video", "error");
  } finally {
    setDialogLoading(false);
  }
};
  // const handleEditSubmit = async (e) => {
  //   e.preventDefault();
  //   setDialogLoading(true);

  //   try {
  //     const submitData = new FormData();
  //     submitData.append("title", editFormData.title);
  //     submitData.append("type", "link");
  //     submitData.append("url", editFormData.url);
  //     submitData.append("userId", userId);

  //     const res = await fetch(
  //       `${URL}/api/charcha/edit-video/${editingVideo._id}`,
  //       {
  //         method: "POST",
  //         body: submitData,
  //       }
  //     );

  //     const result = await res.json();

  //     if (result.success) {
  //       setEditDialogOpen(false);
  //       resetEditForm();
  //       showAlert("Video updated successfully!");
  //       fetchVideos();
  //     } else {
  //       showAlert(result.message, "error");
  //     }
  //   } catch (error) {
  //     showAlert("Error updating video", "error");
  //   } finally {
  //     setDialogLoading(false);
  //   }
  // };
const handleEditSubmit = async (e) => {
  e.preventDefault();
  setDialogLoading(true);

  // ADD THIS CHECK
  if (!userId) {
    console.error("User ID is undefined. User might not be logged in.");
    showAlert("Error: User not logged in. Please login again.", "error");
    setDialogLoading(false);
    return;
  }

  try {
    const submitData = new FormData();
    submitData.append("title", editFormData.title);
    submitData.append("type", "link");
    submitData.append("url", editFormData.url);
    submitData.append("userId", userId); // Make sure this is not undefined

    const res = await fetch(
      `${URL}/api/charcha/edit-video/${editingVideo._id}`,
      {
        method: "POST",
        body: submitData,
      }
    );

    const result = await res.json();

    if (result.success) {
      setEditDialogOpen(false);
      resetEditForm();
      showAlert("Video updated successfully!");
      fetchVideos();
    } else {
      showAlert(result.message, "error");
    }
  } catch (error) {
    showAlert("Error updating video", "error");
  } finally {
    setDialogLoading(false);
  }
};
  const handleDeleteVideo = async () => {
    if (!selectedVideo || !selectedVideo._id) {
      console.error(
        "Cannot delete: selectedVideo is null or missing _id",
        selectedVideo
      );
      showAlert("❌ Error: No video selected for deletion", "error");
      setDeleteDialogOpen(false);
      setDialogLoading(false);
      return;
    }

    setDialogLoading(true);
    try {
      const videoIdToDelete = selectedVideo._id;
      const deleteUrl = `${URL}/api/charcha/delete-video/${videoIdToDelete}/${userId}`;

      const res = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      let result;
      try {
        result = await res.json();
      } catch (jsonError) {
        console.error("Error parsing JSON response:", jsonError);
        throw new Error("Invalid response from server");
      }

      if (!res.ok) {
        throw new Error(
          result.message || `Server responded with status: ${res.status}`
        );
      }

      if (result.success) {
        setDeleteDialogOpen(false);
        setMenuAnchor(null);
        setSelectedVideo(null);
        showAlert("🗑️ Video deleted successfully!");
        fetchVideos();
      } else {
        showAlert(result.message || "❌ Failed to delete video", "error");
      }
    } catch (error) {
      console.error("❌ Delete error:", error);
      showAlert(`❌ Error deleting video: ${error.message}`, "error");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleMenuOpen = (event, video) => {
    setMenuAnchor(event.currentTarget);
    setSelectedVideo(video);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditClick = () => {
    if (selectedVideo && selectedVideo._id) {
      setEditingVideo(selectedVideo);
      setEditFormData({
        title: selectedVideo.title || "",
        url: selectedVideo.link || "",
      });
      setEditDialogOpen(true);
      setMenuAnchor(null);
    } else {
      console.error("No video selected for editing");
      showAlert("❌ No video selected for editing", "error");
      setMenuAnchor(null);
    }
  };

  const handleDeleteClick = () => {
    if (selectedVideo && selectedVideo._id) {
      setDeleteDialogOpen(true);
      setMenuAnchor(null);
    } else {
      console.error("No video selected for deletion");
      showAlert("❌ No video selected for deletion", "error");
      setMenuAnchor(null);
    }
  };

  const getYouTubeThumbnail = (url) => {
    if (!url || typeof url !== "string") {
      return null;
    }

    try {
      // Enhanced YouTube URL patterns
      const youtubeRegex =
        /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
      const match = url.match(youtubeRegex);

      if (match && match[1]) {
        const videoId = match[1];
        // Try different thumbnail qualities
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      }

      return null;
    } catch (error) {
      console.error("Error getting YouTube thumbnail:", error);
      return null;
    }
  };

  const renderVideoCard = (video) => {
    const videoLink = video?.link;
    const thumbnail = videoLink ? getYouTubeThumbnail(videoLink) : null;

    return (
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          transition: "all 0.3s ease-in-out",
          height: 270, // Increased height to accommodate 16:9 ratio
          width: 320,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 16px 32px rgba(0,0,0,0.15)",
            "& .video-actions": {
              opacity: 1,
              transform: "scale(1)",
            },
            "& .thumbnail-overlay": {
              opacity: 1,
            },
          },
        }}
      >
        {/* Action Menu Button */}
        <IconButton
          className="video-actions"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            bgcolor: "rgba(255,255,255,0.95)",
            opacity: 0.7,
            transform: "scale(0.9)",
            transition: "all 0.3s ease",
            zIndex: 20,
            width: 36,
            height: 36,
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(0,0,0,0.1)",
            "&:hover": {
              bgcolor: "white",
              opacity: 1,
              transform: "scale(1.1)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e, video);
          }}
        >
          <MoreVert sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Thumbnail Overlay on Hover */}
        <Box
          className="thumbnail-overlay"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 180, // Match thumbnail height
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 100%)",
            opacity: 0,
            transition: "opacity 0.3s ease",
            zIndex: 15,
            pointerEvents: "none",
          }}
        />

        {/* YouTube Thumbnail - Perfect 16:9 ratio */}
        <Box
          sx={{
            position: "relative",
            height: 180, // 16:9 ratio height (320/1.777 = ~180)
            width: "100%",
            cursor: "pointer",
            overflow: "hidden",
            backgroundColor: colors.lightGray,
            flexShrink: 0,
          }}
          onClick={() => videoLink && window.open(videoLink, "_blank")}
        >
          {thumbnail ? (
            <>
              <Box
                component="img"
                src={thumbnail}
                alt={video.title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover", // This ensures no black bars
                  transition: "transform 0.4s ease",
                  "&:hover": {
                    transform: "scale(1.08)",
                  },
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  bgcolor: "rgba(229, 57, 53, 0.9)",
                  width: 60,
                  height: 60,
                  backdropFilter: "blur(8px)",
                  "&:hover": {
                    bgcolor: "rgba(229, 57, 53, 1)",
                    transform: "translate(-50%, -50%) scale(1.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <PlayArrow sx={{ color: "white", fontSize: 32 }} />
              </IconButton>
            </>
          ) : (
            <Box
              sx={{
                height: "100%",
                bgcolor: colors.lightGray,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <YouTube sx={{ fontSize: 48, color: colors.darkGray, mb: 1 }} />
              <Typography variant="body2" color={colors.darkGray}>
                YouTube Video
              </Typography>
            </Box>
          )}
        </Box>

        <CardContent
          sx={{
            p: 2,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            // Remove fixed height and let it grow naturally
            minHeight: 0, // Important for proper flex behavior
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.4,
              fontSize: "0.95rem",
              minHeight: "2.8rem",
              mb: 1, // Small margin below title
            }}
          >
            {video.title || "Untitled Video"}
          </Typography>

          {/* This will naturally fill the remaining space and stay at bottom */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              // mt: "auto", // This pushes it to bottom but only if there's space
              //pt: 1, // Small padding top to separate from title
              mt: -1,
            }}
          >
            <YouTube sx={{ color: "red", fontSize: 20, mr: 1 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              Watch on YouTube
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Custom Alert Component
  const CustomAlert = ({ open, severity, message, onClose }) => {
    if (!open) return null;

    const isSuccess = severity === "success";
    const borderColor = isSuccess ? colors.limeGreen : colors.deleteBg;
    const iconColor = isSuccess ? colors.darkGreen : colors.red;
    const buttonColor = isSuccess ? colors.darkGreen : colors.red;
    const buttonHoverColor = isSuccess ? colors.green : colors.darkRed;

    return (
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          width: "auto",
          minWidth: 400,
          maxWidth: "90vw",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            border: `3px solid ${borderColor}`,
            borderRadius: 4,
            padding: 0,
            boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header Strip */}
          <Box
            sx={{
              backgroundColor: borderColor,
              padding: "16px 24px",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "white",
                flexShrink: 0,
              }}
            >
              {isSuccess ? (
                <CheckCircle sx={{ color: iconColor, fontSize: 22 }} />
              ) : (
                <Cancel sx={{ color: iconColor, fontSize: 22 }} />
              )}
            </Box>

            {/* Title */}
            <Typography
              sx={{
                fontSize: "1.3rem",
                fontWeight: 700,
                color: "white",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {isSuccess ? "Success" : "Error"}
            </Typography>
          </Box>

          {/* Message Content */}
          <Box
            sx={{
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Message Text */}
            <Typography
              sx={{
                fontSize: "1.1rem",
                fontWeight: 500,
                color: colors.darkBlack,
                lineHeight: 1.5,
                textAlign: "center",
              }}
            >
              {message}
            </Typography>

            {/* OK Button */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: buttonColor,
                  color: "white",
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 4,
                  py: 1,
                  fontSize: "1rem",
                  minWidth: 120,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: buttonHoverColor,
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

   return (
    <Box
      sx={{
        height: "100vh",  // CHANGE THIS from "100%" to "100vh"
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.darkWhite,
        overflow: "hidden",  // ADD THIS LINE
      }}
    >
      <CustomAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        onClose={handleCloseAlert}
      />

      {/* Header */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "white",
          color: colors.darkBlack,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            minHeight: "64px !important",
          }}
        >
          {/* Single Tab for Videos */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              mr: 2,
            }}
          >
            <Tabs
              value={0}
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.green,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  minWidth: "auto",
                  px: { xs: 2, sm: 3 },
                  minHeight: "48px",
                  whiteSpace: "nowrap",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: colors.green,
                },
              }}
            >
              <Tab
                icon={
                  <YouTube
                    sx={{ mr: 1, fontSize: { xs: "18px", sm: "20px" } }}
                  />
                }
                iconPosition="start"
                label="All Videos"
              />
            </Tabs>
          </Box>

          {/* Add Video Button */}
          <Box sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
              sx={{
                background: colors.darkGreen,
                "&:hover": {
                  background: colors.greenGradientAlt,
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(76, 175, 80, 0.3)",
                },
                fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.9rem" },
                padding: { xs: "8px 12px", sm: "10px 20px", md: "11px 22px" },
                minWidth: { xs: "auto", sm: "140px" },
                borderRadius: 4,
                fontWeight: 600,
                transition: "all 0.3s ease",
                whiteSpace: "nowrap",
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Add Video
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: "inline", sm: "none" } }}
              >
                Add
              </Box>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Videos Content */}
        {/* Videos Content */}
      <Box
        ref={videosContainerRef}
        sx={{
          flex: 1,  // CHANGE from flexGrow: 1 to flex: 1
          minHeight: 0,  // ADD THIS - CRUCIAL FOR FLEX SCROLLING
          overflowY: 'auto',
          overflowX: 'hidden',  // ADD THIS
          p: 3,
        }}
      >
        {/* Search Results Info */}
        {searchText && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Search results for: <strong>"{searchText}"</strong>
              {filteredVideos.length > 0 && ` (${filteredVideos.length} found)`}
            </Typography>
          </Box>
        )}

        {/* Loading State */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress
                size={60}
                sx={{
                  color: colors.green,
                  mb: 2,
                }}
              />
              <Typography variant="h6" color="text.secondary">
                {searchText ? "Searching videos..." : "Loading videos..."}
              </Typography>
            </Box>
          </Box>
        ) : (
          // Videos Grid - Enhanced spacing for even distribution
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Grid
              container
              spacing={3}
              sx={{
                maxWidth: "1400px", // Limit maximum width for better spacing
                margin: "0 auto",
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              {paginatedVideos.length > 0 ? (
                paginatedVideos.map((video) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={video._id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-evenly",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: "380px", // Consistent max width for all cards
                        minWidth: "280px", // Minimum width for smaller screens
                      }}
                    >
                      {renderVideoCard(video)}
                    </Box>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <YouTube
                      sx={{ fontSize: 64, color: colors.midGray, mb: 2 }}
                    />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      {searchText
                        ? "No YouTube videos match your search"
                        : "No YouTube videos found"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add YouTube video links to get started
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#2c3e50",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                  minWidth: "42px",
                  height: "42px",
                  margin: "0 4px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  },
                },
                "& .MuiPaginationItem-page": {
                  "&.Mui-selected": {
                    backgroundColor: colors.darkGreen,
                    color: colors.white,
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    border: `2px solid ${colors.darkGreen}`,
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                    "&:hover": {
                      backgroundColor: colors.green,
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
                    },
                  },
                },
                "& .MuiPaginationItem-ellipsis": {
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  color: "#666",
                },
                "& .MuiPaginationItem-previousNext": {
                  border: `2px solid ${colors.green}`,
                  backgroundColor: "transparent",
                  color: colors.green,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: colors.green,
                    color: colors.white,
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                  },
                  "&.Mui-disabled": {
                    border: "1px solid #e0e0e0",
                    color: "#bdbdbd",
                    backgroundColor: "#fafafa",
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            minWidth: 140,
            overflow: "hidden",
          },
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: colors.red }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: colors.red }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Video Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          if (!dialogLoading) {
            setOpenDialog(false);
            resetForm();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Add YouTube Video
            </Typography>
            <IconButton
              onClick={() => {
                if (!dialogLoading) {
                  setOpenDialog(false);
                  resetForm();
                }
              }}
              disabled={dialogLoading}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Video Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              disabled={dialogLoading}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="YouTube Video URL"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              required
              disabled={dialogLoading}
              placeholder="https://www.youtube.com/watch?v=..."
              sx={{ mb: 3 }}
              helperText="Paste the full YouTube video URL"
            />

            <DialogActions sx={{ px: 0 }}>
              <Button
                onClick={() => {
                  if (!dialogLoading) {
                    setOpenDialog(false);
                    resetForm();
                  }
                }}
                disabled={dialogLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={dialogLoading || !formData.title || !formData.url}
                sx={{
                  background: colors.greenGradient,
                  "&:hover": { background: colors.greenGradientAlt },
                  minWidth: 100,
                }}
              >
                {dialogLoading ? <CircularProgress size={20} /> : "Add"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Video Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          if (!dialogLoading) {
            setEditDialogOpen(false);
            resetEditForm();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Edit YouTube Video
            </Typography>
            <IconButton
              onClick={() => {
                if (!dialogLoading) {
                  setEditDialogOpen(false);
                  resetEditForm();
                }
              }}
              disabled={dialogLoading}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleEditSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Video Title"
              name="title"
              value={editFormData.title}
              onChange={handleEditInputChange}
              required
              disabled={dialogLoading}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="YouTube Video URL"
              name="url"
              value={editFormData.url}
              onChange={handleEditInputChange}
              required
              disabled={dialogLoading}
              placeholder="https://www.youtube.com/watch?v=..."
              sx={{ mb: 3 }}
              helperText="Paste the full YouTube video URL"
            />

            <DialogActions sx={{ px: 0 }}>
              <Button
                onClick={() => setEditDialogOpen(false)}
                disabled={dialogLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  dialogLoading || !editFormData.title || !editFormData.url
                }
                sx={{
                  background: colors.greenGradient,
                  "&:hover": { background: colors.greenGradientAlt },
                  minWidth: 100,
                }}
              >
                {dialogLoading ? <CircularProgress size={20} /> : "Update"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!dialogLoading) {
            setDeleteDialogOpen(false);
            setSelectedVideo(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the video "{selectedVideo?.title}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedVideo(null);
            }}
            disabled={dialogLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteVideo}
            variant="contained"
            disabled={dialogLoading}
            sx={{
              background: colors.red,
              "&:hover": { background: colors.darkRed },
            }}
          >
            {dialogLoading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Videos;
